// src/ui/screens/Onboarding.tsx
import { useState } from "react";
import { colors } from "../design-system/colors";
import { fontFamily, typeScale } from "../design-system/typography";
import { space, radius, minTapTarget } from "../design-system/spacing";

export interface OnboardingResult {
  heightCm: number;
  massKg: number;
  sex: "male" | "female";
}

const STORAGE_KEY = "kinemalens_user_anthropometry";

export function loadStoredAnthropometry(): OnboardingResult | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? (JSON.parse(raw) as OnboardingResult) : null;
}

export default function Onboarding({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState<0 | 1>(0);
  const [heightCm, setHeightCm] = useState(170);
  const [massKg, setMassKg] = useState(65);
  const [sex, setSex] = useState<"male" | "female">("male");

  const handleSave = () => {
    const result: OnboardingResult = { heightCm, massKg, sex };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(result));
    onComplete();
  };

  return (
    <div style={styles.container}>
      <div style={styles.heroMark} aria-hidden="true">
        <LensMark />
      </div>

      {step === 0 && (
        <>
          <h1 style={styles.headline}>
            触れるのは、
            <br />
            ボタン一つ。
          </h1>
          <p style={styles.sub}>
            動いているのは、研究室レベルのバイオメカニクス計算です。
            身長と体重だけ教えてください。裏側の計算は、こちらで引き受けます。
          </p>
          <button style={styles.primaryButton} onClick={() => setStep(1)}>
            はじめる
          </button>
        </>
      )}

      {step === 1 && (
        <>
          <h2 style={styles.h2}>体格を教えてください</h2>
          <p style={styles.captionText}>
            腰椎への負荷は体格によって大きく変わります。この情報は端末内にのみ保存され、外部へは送信されません。
          </p>

          <label style={styles.label} htmlFor="height">
            身長
            <span style={styles.readout}>{heightCm} cm</span>
          </label>
          <input
            id="height"
            type="range"
            min={140}
            max={210}
            value={heightCm}
            onChange={(e) => setHeightCm(Number(e.target.value))}
            style={styles.slider}
          />

          <label style={styles.label} htmlFor="mass">
            体重
            <span style={styles.readout}>{massKg} kg</span>
          </label>
          <input
            id="mass"
            type="range"
            min={35}
            max={150}
            value={massKg}
            onChange={(e) => setMassKg(Number(e.target.value))}
            style={styles.slider}
          />

          <div style={styles.sexRow}>
            {(["male", "female"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSex(s)}
                style={{
                  ...styles.sexButton,
                  borderColor: sex === s ? colors.amber : colors.surfaceBorder,
                  color: sex === s ? colors.amber : colors.textSecondary,
                }}
              >
                {s === "male" ? "男性" : "女性"}
              </button>
            ))}
          </div>

          <button style={styles.primaryButton} onClick={handleSave}>
            計測へ進む
          </button>
        </>
      )}
    </div>
  );
}

function LensMark() {
  return (
    <svg width="88" height="88" viewBox="0 0 88 88" fill="none">
      <circle cx="44" cy="44" r="38" stroke={colors.amber} strokeWidth="5" strokeDasharray="180 60" />
      <circle cx="44" cy="44" r="24" stroke={colors.textPrimary} strokeWidth="2.5" />
      <circle cx="44" cy="44" r="11" fill={colors.amberStrong} />
      <circle cx="40" cy="40" r="3.2" fill={colors.textPrimary} />
    </svg>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: `${space.xxl} ${space.lg}`,
    fontFamily: fontFamily.ui,
    color: colors.textPrimary,
    maxWidth: 480,
    margin: "0 auto",
  },
  heroMark: { marginBottom: space.lg },
  headline: {
    fontSize: typeScale.display.size,
    lineHeight: typeScale.display.lineHeight,
    fontWeight: typeScale.display.weight,
    margin: 0,
    marginBottom: space.md,
  },
  h2: {
    fontSize: typeScale.h1.size,
    fontWeight: typeScale.h1.weight,
    marginBottom: space.sm,
  },
  sub: {
    fontSize: typeScale.body.size,
    lineHeight: typeScale.body.lineHeight,
    color: colors.textSecondary,
    marginBottom: space.xl,
    maxWidth: "34ch",
  },
  captionText: {
    fontSize: typeScale.caption.size,
    color: colors.textSecondary,
    marginBottom: space.lg,
  },
  label: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: typeScale.body.size,
    color: colors.textSecondary,
    marginBottom: space.xs,
    marginTop: space.md,
  },
  readout: {
    fontFamily: fontFamily.numeric,
    color: colors.textPrimary,
    fontWeight: 600,
  },
  slider: {
    width: "100%",
    accentColor: colors.amber,
    height: minTapTarget,
  },
  sexRow: { display: "flex", gap: space.sm, marginTop: space.lg },
  sexButton: {
    flex: 1,
    minHeight: minTapTarget,
    borderRadius: radius.md,
    border: "1.5px solid",
    background: "transparent",
    fontSize: typeScale.body.size,
    fontFamily: fontFamily.ui,
  },
  primaryButton: {
    marginTop: space.xl,
    minHeight: 56,
    borderRadius: radius.full,
    border: "none",
    background: colors.amber,
    color: colors.bg,
    fontSize: typeScale.h2.size,
    fontWeight: 700,
    fontFamily: fontFamily.ui,
    cursor: "pointer",
  },
};
